// src/services/metricsApi.js - Refactored with AccessManager authentication pattern

import accessManager from '../Auth/Access'; // Import the AccessManager singleton

/**
 * Metrics API Service - Using AccessManager for authentication
 */
export const metricsAPI = {
  /**
   * Get performance metrics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period type: 'today', 'week', 'month', 'quarter', 'year', 'custom'
   * @param {string} params.start_date - Start date for custom period (YYYY-MM-DD)
   * @param {string} params.end_date - End date for custom period (YYYY-MM-DD)
   * @returns {Promise} Performance metrics data
   */
  getPerformanceMetrics: async (params = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/metrics/performance/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    return response;
  },

  /**
 * Get individual member analysis for a specific group
 * @param {number} groupId - Group ID
 * @param {Object} params - Query parameters
 * @param {string} params.period - Period type: 'today', 'week', 'month', 'quarter', 'year', 'custom'
 * @param {string} params.start_date - Start date for custom period (YYYY-MM-DD)
 * @param {string} params.end_date - End date for custom period (YYYY-MM-DD)
 * @returns {Promise} Member analysis data for the group
 */
  getGroupMemberAnalysis: async (groupId, params = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/metrics/group-member-analysis/${groupId}/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    return response;
  },

  /**
   * Get book portfolio data (current balances and risk assessment for all groups)
   * @returns {Promise} Book portfolio data
   */
  getBookPortfolio: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/metrics/book-portfolio/', {
      method: 'GET'
    });

    return response;
  },
    /**
   * Get closed analysis data (detailed group financial breakdown)
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period type: 'today', 'week', 'month', 'quarter', 'year', 'custom'
   * @param {string} params.start_date - Start date for custom period (YYYY-MM-DD)
   * @param {string} params.end_date - End date for custom period (YYYY-MM-DD)
   * @returns {Promise} Closed analysis data with group-level breakdown
   */
  getClosedAnalysis: async (params = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/metrics/closed-analysis/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    return response;
  },

  /**
   * Get financial metrics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period type: 'today', 'week', 'month', 'quarter', 'year', 'custom'
   * @param {string} params.start_date - Start date for custom period (YYYY-MM-DD)
   * @param {string} params.end_date - End date for custom period (YYYY-MM-DD)
   * @returns {Promise} Financial metrics data
   */
  getFinancialMetrics: async (params = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/metrics/financial/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    return response;
  },

    /**
   * Get active analysis groups
   * @returns {Promise} Active analysis groups data
   */
  getActiveAnalysisGroups: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/metrics/active-analysis/', {
      method: 'GET'
    });

    return response;
  },

  /**
   * Get cash flow analysis
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period type: 'today', 'week', 'month', 'quarter', 'year', 'custom'
   * @param {string} params.start_date - Start date for custom period (YYYY-MM-DD)
   * @param {string} params.end_date - End date for custom period (YYYY-MM-DD)
   * @returns {Promise} Cash flow data
   */
  getCashFlow: async (params = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/metrics/cash-flow/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    return response;
  },

  /**
   * Get portfolio overview
   * @returns {Promise} Portfolio overview data
   */
  getPortfolioOverview: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/metrics/portfolio/', {
      method: 'GET'
    });

    return response;
  },

  /**
   * Get comprehensive metrics (all metrics at once)
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period type: 'today', 'week', 'month', 'quarter', 'year', 'custom'
   * @param {string} params.start_date - Start date for custom period (YYYY-MM-DD)
   * @param {string} params.end_date - End date for custom period (YYYY-MM-DD)
   * @returns {Promise} Comprehensive metrics data
   */
  getComprehensiveMetrics: async (params = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/metrics/comprehensive/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    return response;
  },
  // Add to metricsAPI object in api.js

/**
 * Get defaulters summary
 * @param {number} groupId - Optional group ID to filter by specific group
 * @returns {Promise} Defaulters summary data
 */
getDefaultersSummary: async (groupId = null) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const endpoint = groupId 
    ? `/metrics/defaulters/summary/?group_id=${groupId}`
    : '/metrics/defaulters/summary/';
    
  const response = await accessManager.request(endpoint, {
    method: 'GET'
  });

  return response;
},

/**
 * Get detailed defaulter information
 * @param {number} defaulterId - Defaulter record ID
 * @returns {Promise} Detailed defaulter information
 */
getDefaulterDetails: async (defaulterId) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const response = await accessManager.request(`/metrics/defaulters/${defaulterId}/`, {
    method: 'GET'
  });

  return response;
},

/**
 * Check and update defaulters
 * @param {Object} params - Query parameters
 * @param {number} params.group_id - Optional group ID
 * @param {number} params.member_id - Optional member ID
 * @returns {Promise} Defaulter check results
 */
checkDefaulters: async (params = {}) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const endpoint = `/metrics/defaulters/check/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await accessManager.request(endpoint, {
    method: 'POST'
  });

  return response;
},

/**
 * Get group defaulters report
 * @param {number} groupId - Group ID
 * @returns {Promise} Comprehensive defaulters report for the group
 */
getGroupDefaultersReport: async (groupId) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const response = await accessManager.request(`/metrics/groups/${groupId}/defaulters/report/`, {
    method: 'GET'
  });

  return response;
},

/**
 * Manually resolve a defaulter
 * @param {number} defaulterId - Defaulter record ID
 * @param {string} reason - Reason for manual resolution
 * @returns {Promise} Resolution result
 */
resolveDefaulter: async (defaulterId, reason) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const response = await accessManager.request(`/metrics/defaulters/${defaulterId}/resolve/`, {
    method: 'POST',
    body: JSON.stringify({ reason })
  });

  return response;
},
/**
 * Get all groups ratings for a specific month
 * @param {Object} params - Query parameters
 * @param {number} params.year - Year (e.g., 2024)
 * @param {number} params.month - Month (1-12)
 * @returns {Promise} Group ratings data
 */
getGroupRatings: async (params = {}) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const endpoint = `/metrics/groups/ratings/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await accessManager.request(endpoint, {
    method: 'GET'
  });

  return response;
},

/**
 * Get individual group rating with trends
 * @param {number} groupId - Group ID
 * @param {Object} params - Query parameters
 * @param {string} params.target_month - Target month in YYYY-MM-DD format
 * @returns {Promise} Group rating data with trends
 */
getGroupRating: async (groupId, params = {}) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const endpoint = `/metrics/groups/${groupId}/rating/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await accessManager.request(endpoint, {
    method: 'GET'
  });

  return response;
},

/**
 * Get group rating history
 * @param {number} groupId - Group ID
 * @param {Object} params - Query parameters
 * @param {number} params.months - Number of months to retrieve
 * @returns {Promise} Group rating history
 */
getGroupRatingHistory: async (groupId, params = {}) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const endpoint = `/metrics/groups/${groupId}/rating/history/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await accessManager.request(endpoint, {
    method: 'GET'
  });

  return response;
},

/**
 * Get leaderboard for a specific month
 * @param {Object} params - Query parameters
 * @param {string} params.target_month - Target month in YYYY-MM-DD format
 * @param {number} params.limit - Number of top groups to return
 * @returns {Promise} Leaderboard data
 */
getGroupRatingsLeaderboard: async (params = {}) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const endpoint = `/metrics/groups/ratings/leaderboard/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await accessManager.request(endpoint, {
    method: 'GET'
  });

  return response;
},

/**
 * Recalculate group ratings
 * @param {Object} data - Request body
 * @param {number} data.group_id - Optional specific group ID
 * @param {string} data.target_month - Optional target month in YYYY-MM-DD format
 * @returns {Promise} Recalculation result
 */
recalculateGroupRatings: async (data = {}) => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }

  const response = await accessManager.request('/metrics/groups/ratings/recalculate/', {
    method: 'POST',
    body: JSON.stringify(data)
  });

  return response;
},
};

// Utility functions
export const metricsUtils = {
  /**
   * Format date for API
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date (YYYY-MM-DD)
   */
  formatDate: (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  /**
   * Get metrics for a specific date range
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @returns {Promise} Metrics data for the date range
   */
  getMetricsByDateRange: async (startDate, endDate) => {
    const params = {
      period: 'custom',
      start_date: metricsUtils.formatDate(startDate),
      end_date: metricsUtils.formatDate(endDate),
    };
    return metricsAPI.getComprehensiveMetrics(params);
  },

  /**
   * Get today's metrics
   * @returns {Promise} Today's metrics data
   */
  getTodayMetrics: async () => {
    return metricsAPI.getComprehensiveMetrics({ period: 'today' });
  },

  /**
   * Get this week's metrics
   * @returns {Promise} This week's metrics data
   */
  getWeekMetrics: async () => {
    return metricsAPI.getComprehensiveMetrics({ period: 'week' });
  },

  /**
   * Get this month's metrics
   * @returns {Promise} This month's metrics data
   */
  getMonthMetrics: async () => {
    return metricsAPI.getComprehensiveMetrics({ period: 'month' });
  },

  /**
   * Get this quarter's metrics
   * @returns {Promise} This quarter's metrics data
   */
  getQuarterMetrics: async () => {
    return metricsAPI.getComprehensiveMetrics({ period: 'quarter' });
  },

  /**
   * Get this year's metrics
   * @returns {Promise} This year's metrics data
   */
  getYearMetrics: async () => {
    return metricsAPI.getComprehensiveMetrics({ period: 'year' });
  }
};

// Authorization utilities for UI components
export const metricsAuthUtils = {
  // Check if user is authenticated (required for all actions)
  isAuthenticated: () => accessManager.isAuthenticated(),
  
  // Get current user info for UI display
  getCurrentUser: () => accessManager.getCurrentUser(),
  
  // Get user role for UI customization
  getUserRole: () => {
    const user = accessManager.getCurrentUser();
    return user ? user.role : null;
  },
  
  // Get user branch info for UI display
  getUserBranch: () => accessManager.getUserBranch(),
  getUserBranchId: () => accessManager.getUserBranchId(),
  
  // Helper methods for UI role-based display
  isAdmin: () => accessManager.isAdmin(),
  isSecretary: () => accessManager.isSecretary(),
  isUser: () => accessManager.isUser(),
  
  // Generic permission checker
  hasRole: (role) => accessManager.hasRole(role),
  hasAnyRole: (roles) => accessManager.hasAnyRole(roles),
  
  // Check if user can view metrics (typically all authenticated users)
  canViewMetrics: () => accessManager.isAuthenticated(),
  
  // Check if user can export metrics (might be restricted)
  canExportMetrics: () => accessManager.hasAnyRole(['admin', 'secretary'])
};

// Formatting utilities
export const metricsFormatters = {
  /**
   * Format currency
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default: 'KES')
   * @returns {string} Formatted currency string
   */
  formatCurrency: (amount, currency = 'KES') => {
    return `${currency} ${parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  },

  /**
   * Format percentage
   * @param {number} value - Value to format as percentage
   * @param {number} decimals - Number of decimal places (default: 2)
   * @returns {string} Formatted percentage string
   */
  formatPercentage: (value, decimals = 2) => {
    return `${parseFloat(value || 0).toFixed(decimals)}%`;
  },

  /**
   * Format date
   * @param {string} dateString - Date string to format
   * @returns {string} Formatted date string
   */
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  },

  /**
   * Format datetime
   * @param {string} dateTimeString - DateTime string to format
   * @returns {string} Formatted datetime string
   */
  formatDateTime: (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  /**
   * Format number with thousand separators
   * @param {number} value - Number to format
   * @param {number} decimals - Number of decimal places (default: 0)
   * @returns {string} Formatted number string
   */
  formatNumber: (value, decimals = 0) => {
    return parseFloat(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
};

// Constants for metrics
export const METRICS_CONSTANTS = {
  PERIODS: {
    TODAY: 'today',
    WEEK: 'week',
    MONTH: 'month',
    QUARTER: 'quarter',
    YEAR: 'year',
    CUSTOM: 'custom'
  },
  
  PERIOD_LABELS: {
    today: 'Today',
    week: 'This Week',
    month: 'This Month',
    quarter: 'This Quarter',
    year: 'This Year',
    custom: 'Custom Range'
  }
};

// Error handler specific to metrics API
export const handleMetricsAPIError = (error) => {
  console.error('Metrics API Error:', error);
  
  // Handle authentication errors
  if (error.message.includes('Authentication') || error.message.includes('401')) {
    accessManager.logout();
    return 'Authentication required. Please log in again.';
  }
  
  // Handle specific metrics errors
  if (error.message.includes('Invalid period')) {
    return 'Invalid time period specified.';
  }
  
  if (error.message.includes('Date range')) {
    return 'Invalid date range. Please check your start and end dates.';
  }
  
  // Return user-friendly error message
  return error.message || 'Failed to fetch metrics data. Please try again.';
};

// Export access manager for direct use if needed
export { accessManager };

// Default export - main API object
export default metricsAPI;